import {
  Directive,
  ElementRef,
  Renderer2,
  AfterViewInit,
  Input
} from '@angular/core';
import { SharedService } from '../services/shared.service';
import { AppState } from '../store/app.state';
import { Store } from '@ngrx/store';
import { setLoadingSpinner } from '../store/actions/shared.action';
import * as JSZip from 'jszip';

@Directive({
  selector: '[downloadID]'
})
export class DownloadDirective implements AfterViewInit {

  @Input() details: any;

  private wrapper!: HTMLElement;
  private overlay!: HTMLElement;
  private isProcessing = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private sharedservice: SharedService,
    private store: Store<AppState>,
  ) { }

  ngAfterViewInit() {

    if (!this.el.nativeElement) return;

    this.createWrapper();
    this.createOverlay();
    this.attachHoverEvents();
  }

  private createWrapper() {
    const element = this.el.nativeElement;

    this.wrapper = this.renderer.createElement('div');
    this.renderer.setStyle(this.wrapper, 'position', 'relative');
    this.renderer.setStyle(this.wrapper, 'display', 'inline-block');

    const parent = element.parentNode!;
    if(parent){
      this.renderer.insertBefore(parent, this.wrapper, element);
      this.renderer.appendChild(this.wrapper, element);
    }    
  }

  private createOverlay() {
    this.overlay = this.renderer.createElement('div');

    Object.entries({
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.55)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      opacity: '0',
      transition: 'opacity 0.2s',
      zIndex: '10',
      fontSize: '14px',
      fontWeight: '500'
    }).forEach(([key, value]) =>
      this.renderer.setStyle(this.overlay, key, value)
    );

    const text = this.renderer.createText('Download ID');
    this.renderer.appendChild(this.overlay, text);

    this.renderer.listen(this.overlay, 'click', () => {
      this.captureAndDownload();
    });

    this.renderer.appendChild(this.wrapper, this.overlay);
  }

  private attachHoverEvents() {
    this.renderer.listen(this.wrapper, 'mouseenter', () => {
      this.renderer.setStyle(this.overlay, 'opacity', '1');
    });

    this.renderer.listen(this.wrapper, 'mouseleave', () => {
      this.renderer.setStyle(this.overlay, 'opacity', '0');
    });
  }

  private async captureAndDownload() {
    this.store.dispatch(setLoadingSpinner({ status: true }));
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      await document.fonts.ready;

      let targetElement = this.el.nativeElement.querySelector('#__main_wrapper') as HTMLElement;
      if (!targetElement) {
        targetElement = this.el.nativeElement.querySelector('.main-wrapper') as HTMLElement;
      }
      if (!targetElement) {
        console.warn('No .main-wrapper or #__main_wrapper found inside this element.');
        this.isProcessing = false;
        return;
      }

      // Clone the entire target element to reset transforms      
      const clonedTarget = this.cloneWithStyles(targetElement);

      // Wrap Card
      const wrapWithMargin = (element: HTMLElement, margin = 0) => {          
        const isPWDBack = element.classList.contains('id-card-back') || 
                    element.querySelector('.id-card-back') !== null;
        if(isPWDBack){
          element.style.transform = 'rotateY(180deg) scaleX(-1)';
          element.style.transformOrigin = 'center center';
        }                               
        element.style.margin = '0'
        const wrapper = document.createElement('div');
        wrapper.style.display = 'flex';        
        wrapper.style.padding = `${margin}px`;
        wrapper.style.boxSizing = 'content-box';   
        wrapper.style.background = 'transparent'          
        wrapper.style.width = 'fit-content';                  
        wrapper.appendChild(element.cloneNode(true));        
        document.body.appendChild(wrapper); 
        return wrapper;
      };

      // Query front and back inside the cloned DOM
      const front: any =
        clonedTarget.querySelector('#__id_front') ||
        clonedTarget.querySelector('.id-card-main') ||
        clonedTarget.querySelector('.card-front');

      const back: any =
        clonedTarget.querySelector('#__id_back') ||
        clonedTarget.querySelector('.id-card-back') ||
        clonedTarget.querySelector('.card-back');

      if (!front || !back) {
        console.warn('Front or Back card not found inside cloned wrapper');
        clonedTarget.remove();
        this.isProcessing = false;
        return;
      }      

      const safeName = `${this.details?.fullname || this.details?.fullName} - ${this.details?.template || 'CARD'}`;

      const frontWithMargin = wrapWithMargin(front.cloneNode(true) as HTMLElement, 0);
      const backWithMargin = wrapWithMargin(back.cloneNode(true) as HTMLElement, 0);      

      // Capture as canvas
      const frontBase64 = await this.sharedservice.htmlCanvas(frontWithMargin);
      const backBase64 = await this.sharedservice.htmlCanvas(backWithMargin);

      // Remove cloned element after capture
      frontWithMargin.remove();
      backWithMargin.remove();
      clonedTarget.remove();

      // Create ZIP
      const zip = new JSZip();
      zip.file(`${safeName} - FRONT.png`, frontBase64.split(',')[1], { base64: true });
      zip.file(`${safeName} - BACK.png`, backBase64.split(',')[1], { base64: true });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      this.store.dispatch(setLoadingSpinner({ status: false }));

    } catch (err) {
      console.error('Download failed', err);
      this.store.dispatch(setLoadingSpinner({ status: false }));
    } finally {
      setTimeout(() => (this.isProcessing = false), 1500);
    }
  }
  private cloneWithStyles(element: HTMLElement): HTMLElement {
    const clone = element.cloneNode(true) as HTMLElement;

    const copyStyles = (source: HTMLElement, target: HTMLElement) => {
      const computed:any = getComputedStyle(source);
      for (const key of computed) {
        target.style.setProperty(key, computed.getPropertyValue(key));
      }

      Array.from(source.children).forEach((child, i) => {
        if (target.children[i]) copyStyles(child as HTMLElement, target.children[i] as HTMLElement);
      });
    };

    copyStyles(element, clone);
    clone.style.position = 'absolute';
    clone.style.left = '0';
    clone.style.top = '0';     
    clone.style.background = 'transparent'        
    document.body.appendChild(clone);
    return clone;
  }


}
